import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { Checkbox } from '@/components/ui/checkbox'
import { Credenza } from '@/components/ui/credenza'
import DynamicCredenzaContent from '@/components/ui/credenza-content'
import { Separator } from '@/components/ui/separator'
import { Text } from '@/components/ui/text'
import { WalletInfo, walletsConfig } from '@/config'
import Footer from '@/containers/connect-wallet-modal/Footer'
import NetworkSelector from '@/containers/connect-wallet-modal/NetworkSelector'
import PathSelector from '@/containers/connect-wallet-modal/PathSelector'
import { WalletItem } from '@/containers/connect-wallet-modal/WalletItem'
import { useApiTokenContext } from '@/context'
import { useCommon, useIsImToken } from '@/hooks'
import { useToast } from '@/hooks/use-toast'
import { useWallet } from '@/hooks/use-wallet'
import { useSolanaAdapters } from '@/hooks/wallets/use-sol-wallets'
import { useEvmAdapters } from '@/hooks/wallets/use-wagmi-wallet'
import { useWalletAdapters } from '@/hooks/wallets/use-wallet-adapter'
import { CommonWallet } from '@/icons'
import { useConnectedCallback, useCurrentToken, useNetwork, useSettings } from '@/store'
import { INetwork } from '@/types'
import { WalletAction } from '@/types/wallet'
import { delay, formatError, urlWithVersion, WALLET_LEDGER } from '@/utils'
import { IS_PROD, SUPPORTED_NETWORK, WALLET_SOLANA, WALLET_TREZOR, WALLET_WAGMI } from '@/utils/constants'

import { useConnect } from 'wagmi'

interface WalletModalProps {
  onClose: () => void
  isOpen: boolean
}

const ConnectWalletModal: React.FC<WalletModalProps> = ({ onClose, isOpen }) => {
  const { toast } = useToast()
  const { listNetwork } = useApiTokenContext()
  const currentToken = useCurrentToken()
  const { connectWallet: connect, wallets: connectedWallets, handleCancelWallet } = useWallet()
  const { acceptedTerms: accepted, setAcceptedTerms: setAccepted } = useSettings()
  const initNetwork = useNetwork()
  const [selectedNetwork, setSelectedNetwork] = useState<INetwork | null>(null)
  const [selectedWalletInfo, setSelectedWallet] = useState<WalletInfo | null>(null)
  const selectedWallet = selectedWalletInfo?.key || null

  const [showCancelButton, setShowCancelButton] = useState(false)
  const connectTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [isConnecting, setIsConnecting] = useState(false)
  const [derivationPath, setDerivationPath] = useState<WalletAction | null>(null)
  const [path, setPath] = useState<string>('')
  const [derivationIndex, setDerivationIndex] = useState('0')
  const { setRecentWallet } = useSettings()
  const { adapters: evmAdapters } = useEvmAdapters()
  const { adapters: solAdapters } = useSolanaAdapters()
  const connectedCallback = useConnectedCallback()
  const connectedWalletInfo = connectedWallets[selectedNetwork?.networkId as SUPPORTED_NETWORK]?.wallet
  const connectedWalletKey = connectedWalletInfo?.key

  const isConnected = connectedWalletKey === selectedWallet || selectedWallet === connectedWalletInfo?.walletId

  const combinedWallets = useWalletAdapters({ selectedNetwork, connectedWalletInfo })
  const isImToken = useIsImToken()

  const supportedNetworks = useMemo(() => {
    if (!isImToken) return listNetwork
    return listNetwork.filter((network) => network.networkId === currentToken?.networkId)
  }, [isImToken, currentToken])

  const getActionByWallet = ({
    selectedNetwork,
    selectedWallet,
    derivationIndex,
  }: {
    selectedNetwork: INetwork | null
    selectedWallet: string | null
    derivationIndex: string
  }) => {
    const actions = selectedNetwork?.actions?.[selectedWallet ?? '']
    if (actions?.length) {
      const defaultAction = actions[0]
      return { path: defaultAction.value.replace('{index}', derivationIndex), derivationPath: defaultAction }
    }
    return { path: '', derivationPath: null }
  }

  useEffect(() => {
    const { derivationPath, path } = getActionByWallet({ selectedNetwork, derivationIndex, selectedWallet })
    setDerivationPath(derivationPath)
    setPath(path)
  }, [selectedNetwork, selectedWallet, derivationIndex])

  useEffect(() => {
    if (!isOpen) {
      setSelectedNetwork(null)
      setSelectedWallet(null)
      return
    }

    const network =
      initNetwork || listNetwork?.find((network) => network.networkId === currentToken?.networkId) || listNetwork?.[0]
    if (network) {
      setSelectedNetwork(network)
    }
  }, [isOpen, currentToken, listNetwork])

  useLayoutEffect(() => {
    if (!selectedNetwork) return
    const wallet = combinedWallets[0]
    setSelectedWallet(wallet?.wallet)
  }, [selectedNetwork])

  const isNetworkSupported = (network: INetwork): boolean => {
    if (!selectedWallet) return true
    return network.wallets?.includes(selectedWallet) || false
  }

  const isNetworkDisabled = (network: INetwork): boolean => {
    return selectedWallet ? !isNetworkSupported(network) : false
  }

  const selectNetwork = (network: INetwork) => {
    if (network.networkId === selectedNetwork?.networkId && !selectedWallet) {
      setSelectedNetwork(null)
      setSelectedWallet(null)
      return
    }
    setSelectedNetwork(network)
  }

  const buildPath = (action: WalletAction | null, index: string): string => {
    if (!action) return ''
    if (action.isCustom) {
      return path
    }
    return action.value.replace('{index}', index)
  }

  const getWalletActions = (): WalletAction[] | null => {
    if (!selectedNetwork?.actions || !selectedWallet) return null
    return selectedNetwork.actions[selectedWallet] || null
  }

  const handleCancel = () => {
    if (connectTimeoutRef.current) {
      clearTimeout(connectTimeoutRef.current)
    }
    setIsConnecting(false)
    setShowCancelButton(false)
    handleCancelWallet()
  }

  const { disconnectWallet } = useCommon()

  const getWalletAdapterByKey = (selectedWallet: string) => {
    const evm = evmAdapters?.find((c) => c.key === selectedWallet)?.adapter
    if (evm) return { adapter: evm, key: WALLET_WAGMI }

    const sol = solAdapters?.find((c) => c.key === selectedWallet)?.adapter
    if (sol) return { adapter: sol, key: WALLET_SOLANA }

    return { key: selectedWallet }
  }

  const handleConnect = async (selectedWallet: string | null, walletInfo: WalletInfo | null) => {
    if (!selectedNetwork || !selectedWallet || !walletInfo || !accepted) {
      return
    }
    if (connectedWalletKey === selectedWallet || selectedWallet === connectedWalletInfo?.walletId) {
      await disconnectWallet(selectedNetwork.networkId)
      return
    }
    setIsConnecting(true)

    connectTimeoutRef.current = setTimeout(() => {
      setShowCancelButton(true)
    }, 5000)

    try {
      if ([WALLET_TREZOR, WALLET_LEDGER].includes(selectedWallet)) {
        const wallet = walletsConfig[selectedNetwork.networkId]?.[selectedWallet]

        const fullPath =
          buildPath(derivationPath, derivationIndex) ||
          getActionByWallet({ selectedWallet, selectedNetwork, derivationIndex }).path

        await connect(wallet, selectedNetwork.networkId, {
          path: fullPath,
          network: selectedNetwork,
        })
      } else {
        const { adapter, key } = getWalletAdapterByKey(selectedWallet)
        if (adapter) reset()
        const wallet = walletInfo?.wallet || walletsConfig[selectedNetwork.networkId]?.[key]
        await connect(wallet, selectedNetwork.networkId, {
          network: selectedNetwork,
          adapter,
          disconnectOthers: true,
        })
      }

      onClose()
      if (connectedCallback) {
        await delay(0) // wait for the modal to close before calling the callback
        await connectedCallback?.()
      }
      setRecentWallet({ network: selectedNetwork.networkId, wallet: selectedWallet })
    } catch (error) {
      if (error instanceof Error && error.message.includes('Redirecting to')) {
        toast({
          title: 'Opening Wallet',
          description: 'Redirecting you to the wallet app...',
          variant: 'default',
        })
      } else {
        console.error('Failed to connect wallet:', error)
        const errorMessage = formatError(error)
        toast({
          title: 'Connection Failed',
          variant: 'destructive',
          description: <div className='text-size-14' dangerouslySetInnerHTML={{ __html: errorMessage as string }} />,
        })
      }
    } finally {
      handleCancel()
    }
  }

  useEffect(() => {
    return () => {
      if (connectTimeoutRef.current) {
        clearTimeout(connectTimeoutRef.current)
      }
    }
  }, [])

  const { reset } = useConnect()
  const wrapClose = () => {
    reset()
    onClose()
  }

  return (
    <Credenza open={isOpen} onOpenChange={wrapClose}>
      <DynamicCredenzaContent open={isOpen} className={`${IS_PROD ? 'max-w-[528px]' : 'max-w-[600px]'} rounded-sm`}>
        <div className='space-y-4 bg-dark-background p-3 md:p-6 md:mb-0 overflow-auto max-h-screen '>
          <Text as='p' size={18}>
            Connect Wallet
          </Text>
          <div className='flex items-center space-x-3'>
            <Checkbox
              id='terms'
              className='size-5'
              checked={accepted}
              onCheckedChange={(status: boolean) => setAccepted(status)}
            />
            <label
              htmlFor='terms'
              className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
            >
              <Text as='span' size={14} className='text-white-48'>
                By connecting a wallet, I confirm that I have read and agree to the updated{' '}
              </Text>
              <Text
                as='a'
                target='_blank'
                color='main'
                href={urlWithVersion('https://optimex.com/files/optimex_tou.pdf')}
                className='pr-1'
              >
                Terms of Use
                <Text as='span' color='white-48'>
                  ,{' '}
                </Text>
              </Text>
              <Text
                as='a'
                target='_blank'
                color='main'
                href={urlWithVersion('https://optimex.com/files/optimex_disclaimers.pdf')}
              >
                Disclaimers
              </Text>
              <Text as='span' color='white-48'>
                {' '}
                and{' '}
              </Text>
              <Text
                as='a'
                target='_blank'
                color='main'
                href={urlWithVersion('https://optimex.com/files/optimex_referral_terms.pdf')}
              >
                Referral and Promotional Program terms
              </Text>
              .
            </label>
          </div>
          <Separator />
          <div className='flex gap-3 md:gap-6'>
            <div className='flex flex-col md:w-[unset] md:self-stretch'>
              <NetworkSelector
                className={accepted ? `` : `opacity-50 pointer-events-none`}
                networks={supportedNetworks}
                onSelectNetwork={selectNetwork}
                isNetworkDisabled={isNetworkDisabled}
                currentToken={currentToken}
                selectedNetwork={selectedNetwork}
                selectedWallet={selectedWallet}
              />

              <PathSelector
                {...{
                  derivationPath,
                  path,
                  setDerivationIndex,
                  setPath,
                  setDerivationPath,
                  derivationIndex,
                  options: getWalletActions(),
                }}
              />
            </div>
            <div className='space-y-2 flex-1 md:min-w-[268px]'>
              <div className='text-size-16 flex gap-1 text-white-48'>
                <CommonWallet className='w-4' />
                <Text as='p' size={14} className='my-auto'>
                  Wallet
                </Text>
              </div>
              <div className='space-y-1 h-[388px] overflow-auto'>
                {combinedWallets.map((item) => {
                  const { wallet, isSupported, key, ...rest } = item
                  return (
                    <WalletItem
                      {...{
                        ...rest,
                        wallet,
                        onClick: () => {
                          if (!isSupported) return
                          setSelectedWallet(wallet)
                          handleConnect(wallet.key, wallet)
                        },
                        isSupported,
                        isSelected: selectedWallet === wallet.key,
                        className: accepted ? `` : `opacity-50 pointer-events-none`,
                      }}
                      key={wallet.key}
                    />
                  )
                })}
              </div>
            </div>
          </div>
          <Footer
            {...{
              accepted,
              setAccepted,
              showCancelButton,
              isConnecting,
              handleCancel,
              handleConnect,
              isConnected,
              selectedNetwork,
              selectedWallet,
              selectedWalletInfo,
            }}
          />
        </div>
      </DynamicCredenzaContent>
    </Credenza>
  )
}

export default ConnectWalletModal
